import { API_URL } from "@/api/constant";
import type {
  IBirthday,
  IWorkAnniversary,
} from "@/interface/dashboard.interface";
import { formatDate, getDaysLeftStyle, getInitials } from "@/utility/helper";
import { Cake } from "lucide-react";

export const TeddyBearIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="6.5" cy="5.5" r="2.5" />
    <circle cx="17.5" cy="5.5" r="2.5" />
    <circle cx="12" cy="13.5" r="7.5" />
    <circle cx="9" cy="12.5" r="1" fill="currentColor" stroke="none" />
    <circle cx="15" cy="12.5" r="1" fill="currentColor" stroke="none" />
    <path d="M10 16.2c.55.5 1.25.8 2 .8s1.45-.3 2-.8" />
  </svg>
);

const RenderEventTable: React.FC<{
  title: string;
  events: (IBirthday | IWorkAnniversary)[];
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
}> = ({ title, events, icon, iconBg }) => {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <h3 className="font-display text-sm font-semibold text-slate-800 flex items-center gap-2">
          <span
            className={`w-7 h-7 rounded-lg ${iconBg} flex items-center justify-center`}
          >
            {icon}
          </span>
          {title}
        </h3>
        <span className="text-xs text-slate-400">{events.length} upcoming</span>
      </div>
      <div className="p-2 sm:p-3">
        {events.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className="text-left px-4 py-2.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="text-left px-4 py-2.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                    Event
                  </th>
                  <th className="text-left px-4 py-2.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="text-left px-4 py-2.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                    Days Left
                  </th>
                </tr>
              </thead>
              <tbody>
                {events?.map((event, index) => {
                  const isBirthday = event?.type === "birthday";
                  const profilePhoto = event?.user?.profilePhoto;
                  const isToday = event?.daysLeft === 0;
                  return (
                    <tr
                      key={index}
                      className={`group ${isToday
                        ? isBirthday
                          ? "bg-pink-50/50"
                          : "bg-violet-50/50"
                        : ""
                        }`}
                    >
                      <td className="px-4 py-3 border-t border-slate-100 group-hover:bg-slate-50/80 rounded-l-lg">
                        <div className="flex items-center gap-3">
                          <div className="relative shrink-0">
                            {profilePhoto ? (
                              <img
                                src={API_URL + "/" + profilePhoto}
                                alt={event.user?.name}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold ${isBirthday
                                  ? "bg-pink-50 text-pink-600"
                                  : "bg-violet-50 text-violet-600"
                                  }`}
                              >
                                {getInitials(event.user?.name)}
                              </div>
                            )}
                          </div>
                          <span className="text-slate-700 font-medium">
                            {event.user?.name || "N/A"}
                          </span>
                          {isToday && (
                            <span
                              className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full animate-pulse ${isBirthday
                                ? "bg-pink-100 text-pink-700"
                                : "bg-violet-100 text-violet-700"
                                }`}
                            >
                              {isBirthday ? (
                                <Cake className="w-3 h-3" strokeWidth={2.5} />
                              ) : (
                                <TeddyBearIcon className="w-3 h-3" />
                              )}
                              Today!
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 border-t border-slate-100 group-hover:bg-slate-50/80">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full ${isBirthday
                            ? "bg-pink-50 text-pink-700 border border-pink-100"
                            : "bg-violet-50 text-violet-700 border border-violet-100"
                            }`}
                        >
                          {isBirthday ? "Birthday" : "Work Anniversary"}
                        </span>
                      </td>
                      <td className="px-4 py-3 border-t border-slate-100 text-slate-500 text-xs group-hover:bg-slate-50/80">
                        {formatDate(event.date)}
                      </td>
                      <td className="px-4 py-3 border-t border-slate-100 group-hover:bg-slate-50/80 rounded-r-lg">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold tabular-nums ${isToday
                            ? isBirthday
                              ? "bg-pink-100 text-pink-700"
                              : "bg-violet-100 text-violet-700"
                            : getDaysLeftStyle(event.daysLeft)
                            }`}
                        >
                          {isToday
                            ? "Today"
                            : event.daysLeft !== undefined &&
                              event.daysLeft !== null
                              ? `${event.daysLeft} day${event.daysLeft !== 1 ? "s" : ""}`
                              : "—"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center text-slate-500 py-8">
            No upcoming {title.toLowerCase()}
          </div>
        )}
      </div>
    </div>
  );
};

export default RenderEventTable;
